import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, BookOpen, PenLine, ClipboardList, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API, MOVEMENT_ICONS } from "@/lib/api";

const ProgressMapPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/journey/progress-map").then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const percent = Math.round((data.completed_count / data.total) * 100);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">Din utveckling</p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3" data-testid="progress-page-title">Min progress</h1>
          </motion.div>

          {/* Main circle */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="flex justify-center mb-12">
            <div className="relative w-44 h-44">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#EBE3DF" strokeWidth="5" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#A0655A" strokeWidth="5"
                  strokeDasharray={`${percent * 2.64} 264`}
                  strokeLinecap="round" className="transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-light text-[#111111]" data-testid="progress-percent">{percent}%</span>
                <span className="text-xs text-[#111111]/40">{data.completed_count}/{data.total} steg</span>
              </div>
            </div>
          </motion.div>

          {/* Movements progress */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-4 mb-12">
            {data.movements.map((m, idx) => {
              const Icon = MOVEMENT_ICONS[idx];
              const mPercent = Math.round((m.completed_layers / m.total_layers) * 100);
              return (
                <div key={m.id} className="bg-white border border-[#EBE3DF] rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${m.is_complete ? 'bg-[#A0655A]' : 'bg-[#FBF5F2]'}`}>
                      {m.is_complete ? <Check className="text-white" size={16} /> : <Icon className="text-[#A0655A]" size={16} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#111111]">{m.title}</p>
                      <p className="text-xs text-[#111111]/40">{m.subtitle}</p>
                    </div>
                    <span className="text-sm text-[#A0655A] font-medium">{m.completed_layers}/{m.total_layers}</span>
                  </div>
                  <div className="w-full bg-[#F9F3EF] rounded-full h-1.5">
                    <div className="bg-[#A0655A] h-1.5 rounded-full transition-all duration-500" style={{ width: `${mPercent}%` }} />
                  </div>
                </div>
              );
            })}
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <p className="text-sm text-[#111111]/40 mb-4 text-center">Din aktivitet</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: BookOpen, label: "Reflektioner", value: data.reflection_count },
                { icon: PenLine, label: "Dagboksinlägg", value: data.diary_count },
                { icon: ClipboardList, label: "Tester gjorda", value: data.test_count },
                { icon: Calendar, label: "Dagar på resan", value: data.days_on_journey },
              ].map(({ icon: Icon, label, value }, idx) => (
                <div key={idx} className="bg-white border border-[#EBE3DF] rounded-xl p-4 text-center">
                  <Icon className="mx-auto text-[#A0655A] mb-2" size={18} />
                  <p className="text-xl font-light text-[#111111]">{value}</p>
                  <p className="text-[10px] text-[#111111]/40 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          {!data.is_journey_complete && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-center mt-10">
              <Button onClick={() => navigate('/dashboard')} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base">
                Fortsätt din resa
              </Button>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProgressMapPage;
